import type React from "react"
import { useState, useEffect, useMemo, memo, useCallback, useRef } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Typography, Space, Card, Tag, Descriptions, Divider } from "antd"
import { LoadingOutlined, ExpandOutlined, CompressOutlined } from "@ant-design/icons"
import { normalizeScore, calculateTotalScore, sortTransfers } from "./utils"

interface ExtractedData {
  amount?: string | number
  coelsaId?: string
  operationNumber?: string
  date?: string
  time?: string
  cuit?: string
  cuil?: string
}

interface TransferScores {
  identifier: number
  amount: number
  time: number
}

interface ComparedTransfer {
  id: number
  score: number
  scores: TransferScores
  coelsaId?: string
  operationNumber?: string
  amount?: number
  dateTime: string
  cuit?: string
  cuil?: string
}

interface ValidationStep {
  metadata: {
    comparedTransfers: ComparedTransfer[]
    extractedFields?: ExtractedData
    detectedPlatform?: string
  }
}

interface ValidationDetails {
  detectedPlatform?: string
  manualAssignment?: boolean
  assignedAt?: Date
  transferId?: number
  matchedTransfer?: MatchedTransfer
  confidenceScores?: Record<string, number>
}

interface MatchedTransfer {
  id: number
  operationNumber: string
  dateTime: Date
  amount: number
  coelsaId?: string
}

interface ValidationAnalysisProps {
  requestData: {
    retryHistory: any[]
    processingSteps: Array<{
      step: string
      status: string
      details: string
      metadata: {
        extractedFields?: Record<string, any>
        comparedTransfers?: Array<ComparedTransfer>
        detectedPlatform?: string
        confidenceScores?: Record<string, number>
        matchedTransferId?: number
        [key: string]: any
      }
      timestamp: string | Date
    }>
  }
}

interface TextComparisonProps {
  extracted?: string
  actual?: string
  score: number
  isDateTime?: boolean
}

interface AnimatedTransfer {
  transfer: ComparedTransfer
  isVisible: boolean
}

interface CharacterComparisonProps {
  char?: string
  matchChar?: string
  isMatch: boolean
}

interface ComparisonFieldProps {
  label: string
  extracted: string
  actual: string
  score: number
  isDateTime?: boolean
}

interface TransferCardProps {
  transfer: ComparedTransfer
  extractedData: ExtractedData
  index: number
  isMatched: boolean
  style?: React.CSSProperties
  isMobile?: boolean
}

const CharacterComparison: React.FC<CharacterComparisonProps> = memo(({ char, matchChar, isMatch }) => {
  return (
    <span
      style={{
        color: isMatch ? "#52c41a" : "#f5222d",
        fontWeight: "bold",
        fontSize: window.innerWidth < 576 ? "13px" : "14px"
      }}
    >
      {char || matchChar}
    </span>
  );
});


const ScoreTag: React.FC<{ score: number }> = memo(({ score }) => {
  const normalizedScore = normalizeScore(score);

  return (
    <Tag color={normalizedScore >= 90 ? "success" : normalizedScore >= 70 ? "warning" : "error"} style={{ whiteSpace: "nowrap" }}>
      {normalizedScore}%
    </Tag>
  );
});



// Improved TextComparison component with special date handling
// Complete TextComparison component with ALL separators matching
const TextComparison: React.FC<TextComparisonProps> = memo(({ extracted, actual, score, isDateTime }) => {
  // IMPORTANTE: Normalizar ANTES de convertir a string para evitar problemas con números
  const safeExtracted = extracted?.toString().trim() || "";
  const safeActual = actual?.toString().trim() || "";
  const isMobile = window.innerWidth < 576;

  // Los logs se manejan más abajo solo para el matched transfer

  // Special handling for date/time fields
  if (isDateTime) {
    // Parse date and time components
    const parseDateTime = (dateStr: string) => {
      const result: {
        day?: string;
        month?: string; 
        year?: string;
        hours?: string;
        minutes?: string;
        seconds?: string;
        ampm?: string;
      } = {};
      
      // Extract all numeric components
      const numbers = dateStr.match(/\d+/g) || [];
      
      // Handle date part
      if (numbers.length >= 3) {
        result.day = numbers[0];
        result.month = numbers[1];
        result.year = numbers[2];
      }
      
      // Handle time part
      if (numbers.length >= 5) {
        result.hours = numbers[3];
        result.minutes = numbers[4];
        if (numbers.length >= 6) {
          result.seconds = numbers[5];
        }
      } else if (numbers.length >= 4) {
        result.hours = numbers[3];
        result.minutes = "00";
      }
      
      // Check for AM/PM
      if (dateStr.match(/[ap]\.?m\.?/i)) {
        result.ampm = dateStr.match(/([ap]\.?m\.?)/i)?.[1] || "";
      }
      
      return result;
    };
    
    const extractedParts = parseDateTime(safeExtracted);
    const actualParts = parseDateTime(safeActual);
    
    // Generate match map for each character
    const matchMap = new Array(Math.max(safeExtracted.length, safeActual.length)).fill(false);
    
    // Helper function to find component in string
    const findComponent = (str: string, component: string | undefined): [number, number] => {
      if (!component) return [-1, -1];
      const start = str.indexOf(component);
      return start >= 0 ? [start, start + component.length] : [-1, -1];
    };
    
    // Match day
    if (extractedParts.day && actualParts.day && 
        extractedParts.day === actualParts.day) {
      const [_, __] = findComponent(safeExtracted, extractedParts.day);
      const [aStart, _day] = findComponent(safeActual, actualParts.day);

      
      if (aStart >= 0) {
        for (let i = 0; i < actualParts.day.length; i++) {
          if (aStart + i < matchMap.length) {
            matchMap[aStart + i] = true;
          }
        }
      }
    }
    
    // Match month (normalize formatting - "3" equals "03")
    if (extractedParts.month && actualParts.month && 
        (extractedParts.month === actualParts.month || 
         parseInt(extractedParts.month) === parseInt(actualParts.month))) {
      const [_, __] = findComponent(safeExtracted, extractedParts.month);
const [aStart, _month] = findComponent(safeActual, actualParts.month);
      
      if (aStart >= 0) {
        for (let i = 0; i < actualParts.month.length; i++) {
          if (aStart + i < matchMap.length) {
            matchMap[aStart + i] = true;
          }
        }
      }
    }
    
    // Match year
    if (extractedParts.year && actualParts.year && 
        extractedParts.year === actualParts.year) {
      const [_, __] = findComponent(safeExtracted, extractedParts.year);
const [aStart, _year] = findComponent(safeActual, actualParts.year);
      
      if (aStart >= 0) {
        for (let i = 0; i < actualParts.year.length; i++) {
          if (aStart + i < matchMap.length) {
            matchMap[aStart + i] = true;
          }
        }
      }
    }
    
    // Match hours (handle 12/24 hour format)
    if (extractedParts.hours && actualParts.hours) {
      let hoursMatch = false;
      
      // Direct match
      if (extractedParts.hours === actualParts.hours) {
        hoursMatch = true;
      } 
      // 24hr to 12hr conversion
      else if (actualParts.ampm && actualParts.ampm.toLowerCase().startsWith('p') && 
               parseInt(extractedParts.hours) === parseInt(actualParts.hours) + 12) {
        hoursMatch = true;
      }
      // 12hr to 24hr conversion
      else if (actualParts.ampm && actualParts.ampm.toLowerCase().startsWith('a') && 
               parseInt(extractedParts.hours) === parseInt(actualParts.hours)) {
        hoursMatch = true;
      }
      
      if (hoursMatch) {
        const [_, __] = findComponent(safeExtracted, extractedParts.hours);
const [aStart, _hours] = findComponent(safeActual, actualParts.hours);
        
        if (aStart >= 0) {
          for (let i = 0; i < actualParts.hours.length; i++) {
            if (aStart + i < matchMap.length) {
              matchMap[aStart + i] = true;
            }
          }
        }
      }
    }
    
    // Match minutes
    if (extractedParts.minutes && actualParts.minutes &&
        extractedParts.minutes === actualParts.minutes) {
      const [_, __] = findComponent(safeExtracted, extractedParts.minutes);
const [aStart, _minutes] = findComponent(safeActual, actualParts.minutes);
      
      if (aStart >= 0) {
        for (let i = 0; i < actualParts.minutes.length; i++) {
          if (aStart + i < matchMap.length) {
            matchMap[aStart + i] = true;
          }
        }
      }
    }
    
    // Match seconds if present
    if (extractedParts.seconds && actualParts.seconds) {
      const [_, __] = findComponent(safeExtracted, extractedParts.seconds);
const [aStart, _seconds] = findComponent(safeActual, actualParts.seconds);
      
      if (aStart >= 0) {
        for (let i = 0; i < actualParts.seconds.length; i++) {
          if (aStart + i < matchMap.length) {
            matchMap[aStart + i] = true;
          }
        }
      }
    }
    
    // Match AM/PM with 24-hour format
    if (actualParts.ampm && parseInt(extractedParts.hours || "0") > 12) {
const [aStart, _ampm] = findComponent(safeActual, actualParts.ampm);
      
      if (aStart >= 0 && actualParts.ampm.toLowerCase().startsWith('p')) {
        for (let i = 0; i < actualParts.ampm.length; i++) {
          if (aStart + i < matchMap.length) {
            matchMap[aStart + i] = true;
          }
        }
      }
    }
    
    // MARK ALL SEPARATORS AS MATCHING (/, :, -, space)
    for (let i = 0; i < safeActual.length; i++) {
      const char = safeActual[i];
      if (["/", ":", "-", " ", ",", "."].includes(char)) {
        matchMap[i] = true;
      }
    }

    // Build character array with match information
    const characters = Array.from({ length: Math.max(safeExtracted.length, safeActual.length) }).map((_, i) => {
      return {
        extracted: i < safeExtracted.length ? safeExtracted[i] : "",
        actual: i < safeActual.length ? safeActual[i] : "",
        isMatch: matchMap[i]
      };
    });
    
    return (
      <Space direction={isMobile ? "vertical" : "horizontal"} align={isMobile ? "start" : "center"} style={{ width: '100%', wordBreak: 'break-word' }}>
        <div className="comparison-text" style={{ 
          fontSize: isMobile ? "13px" : "14px",
          wordBreak: "break-word",
          display: "flex",
          flexWrap: "wrap",
          maxWidth: "100%"
        }}>
          {characters.map((char, i) => (
            <CharacterComparison 
              key={i} 
              char={char.actual} 
              matchChar={char.extracted} 
              isMatch={char.isMatch} 
            />
          ))}
        </div>
        <ScoreTag score={score} />
      </Space>
    );
  }

  // Standard non-date text comparison
  const characters = useMemo(() => {
    // Si ambos están vacíos, retornar array vacío
    if (!safeExtracted && !safeActual) return [];

    // ✅ NUEVO: Si el score es 100%, todo es verde (match perfecto)
    if (score >= 100) {
      const text = safeActual || safeExtracted;
      return Array.from(text).map((char) => ({
        extracted: char,
        actual: char,
        isMatch: true
      }));
    }

    // Si solo uno está vacío, todos los caracteres son no-match
    if (!safeExtracted || !safeActual) {
      const text = safeActual || safeExtracted;
      return Array.from(text).map((char) => ({
        extracted: safeExtracted ? char : "",
        actual: safeActual ? char : "",
        isMatch: false
      }));
    }

    // Normalizar strings para comparación (sin espacios, saltos de línea, guiones, puntos)
    const normalizeForComparison = (str: string) =>
      str.replace(/[\s\n\r\-\.]/g, '').toLowerCase();

    const normalizedExtracted = normalizeForComparison(safeExtracted);
    const normalizedActual = normalizeForComparison(safeActual);

    // Si los normalizados son iguales, todo es verde
    if (normalizedExtracted === normalizedActual) {
      return Array.from(safeActual).map((char) => ({
        extracted: char,
        actual: char,
        isMatch: true
      }));
    }

    // Si no coinciden normalizados, hacer comparación carácter por carácter
    // Extraer solo los caracteres significativos (sin espacios/separadores) de cada string
    const extractedChars: string[] = [];
    const actualChars: string[] = [];

    for (const char of safeExtracted) {
      if (!/[\s\n\r\-\.]/.test(char)) {
        extractedChars.push(char.toLowerCase());
      }
    }

    for (const char of safeActual) {
      if (!/[\s\n\r\-\.]/.test(char)) {
        actualChars.push(char.toLowerCase());
      }
    }

    // Crear mapa de coincidencias para caracteres del actual
    const matchMap = Array(safeActual.length).fill(false);
    let actualCharIndex = 0;

    for (let i = 0; i < safeActual.length; i++) {
      const char = safeActual[i];

      // Espacios, saltos de línea, guiones y puntos siempre son verdes
      if (/[\s\n\r\-\.]/.test(char)) {
        matchMap[i] = true;
        continue;
      }

      // Comparar con el carácter correspondiente del extracted
      if (actualCharIndex < extractedChars.length) {
        const actualCharLower = char.toLowerCase();
        const extractedChar = extractedChars[actualCharIndex];
        matchMap[i] = actualCharLower === extractedChar;
      } else {
        matchMap[i] = false;
      }

      actualCharIndex++;
    }

    return Array.from(safeActual).map((char, i) => ({
      extracted: i < safeExtracted.length ? safeExtracted[i] : "",
      actual: char,
      isMatch: matchMap[i]
    }));
  }, [safeExtracted, safeActual]);

  return (
    <Space direction={isMobile ? "vertical" : "horizontal"} align={isMobile ? "start" : "center"} style={{ width: '100%' }}>
      <div style={{
        wordBreak: "break-word",
        fontSize: isMobile ? "13px" : "14px",
        display: "flex",
        flexWrap: "wrap",
        maxWidth: "100%"
      }}>
        {(!safeExtracted && !safeActual)
          ? <span style={{ color: "#8c8c8c" }}>N/A</span>
          : characters.map((char, i) => (
              <CharacterComparison
                key={i}
                char={char.actual}
                matchChar={char.extracted}
                isMatch={char.isMatch}
              />
            ))}
      </div>
      <ScoreTag score={score} />
    </Space>
  );
});


const ComparisonField: React.FC<ComparisonFieldProps> = memo(({ label, extracted, actual, score, isDateTime }) => {
  const isMobile = window.innerWidth < 576;

  // No loguear aquí, se loguea en TransferCard solo para matched

  return (
    <div className="comparison-field" style={{ marginBottom: isMobile ? '6px' : '6px' }}>
      <Typography.Text
        type="secondary"
        style={{
          display: 'block',
          marginBottom: isMobile ? '2px' : '2px',
          fontSize: isMobile ? '11px' : '12px',
          fontWeight: 500
        }}
      >
        {label}
      </Typography.Text>
      <TextComparison extracted={extracted} actual={actual} score={score} isDateTime={isDateTime} />
    </div>
  );
});

const ExtractedDataCard: React.FC<{ extractedData: ExtractedData }> = memo(({ extractedData }) => {
  const isMobile = window.innerWidth < 576;

  return (
    <Card
      size="small"
      variant="outlined"
      className="extracted-data-card"
      style={{
        marginLeft: 0,
        marginRight: isMobile ? '8px' : '16px'
      }}
      bodyStyle={{
        padding: isMobile ? '8px 12px' : '10px 16px'
      }}
    >
      <Descriptions
        column={1}
        size="small"
        className="extracted-data-descriptions"
        labelStyle={{
          padding: isMobile ? '2px 0 2px 12px' : '3px 0 3px 16px',
          fontSize: isMobile ? '11px' : '12px',
          fontWeight: 500
        }}
        contentStyle={{
          padding: isMobile ? '2px 0 2px 12px' : '3px 0 3px 16px',
          fontSize: isMobile ? '12px' : '13px'
        }}
      >
        {extractedData.amount && (
          <Descriptions.Item label="Monto">
            ${typeof extractedData.amount === "number" ? extractedData.amount.toFixed(2) : extractedData.amount}
          </Descriptions.Item>
        )}
        {extractedData.coelsaId ? (
          <Descriptions.Item label="COELSA ID">
            <span style={{ wordBreak: 'break-all' }}>{extractedData.coelsaId}</span>
          </Descriptions.Item>
        ) : (
          extractedData.operationNumber && (
            <Descriptions.Item label="Número de operación">
              <span style={{ wordBreak: 'break-all' }}>{extractedData.operationNumber}</span>
            </Descriptions.Item>
          )
        )}
        <Descriptions.Item label="Fecha/Hora">
          {extractedData.date || extractedData.time
            ? `${extractedData.date || ""} ${extractedData.time || ""}`.trim()
            : <span style={{ color: "#8c8c8c" }}>N/A</span>}
        </Descriptions.Item>
        {(extractedData.cuit || extractedData.cuil) && (
          <Descriptions.Item label="CUIT/CUIL">
            <span style={{ wordBreak: 'break-all' }}>{extractedData.cuit || extractedData.cuil}</span>
          </Descriptions.Item>
        )}
      </Descriptions>
    </Card>
  );
});

const TransferCard: React.FC<TransferCardProps> = memo(({ transfer, extractedData, index, isMatched, style = {} }) => {
  const totalScore = calculateTotalScore(transfer);
  const isMobile = window.innerWidth < 576;
  const [expanded, setExpanded] = useState(!isMobile || isMatched);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.1 }}
      style={{
        ...style,
        width: "100%",
        marginBottom: isMobile ? "6px" : "8px",
        marginLeft: 0,
        marginRight: isMobile ? '8px' : '16px'
      }}
      className="transfer-card-container"
    >
      <Card
        size="small"
        style={{
          border: isMatched ? "2px solid #52c41a" : undefined,
          background: isMatched ? "#f6ffed" : undefined,
        }}
        className="transfer-card"
        headStyle={{
          padding: isMobile ? '6px 10px' : '8px 14px',
          minHeight: 'auto'
        }}
        bodyStyle={{
          padding: isMobile ? '8px 10px' : '10px 14px'
        }}
        title={
          <Space
            align="center"
            className="transfer-card-title"
            style={{
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: 'space-between',
              width: '100%',
              fontSize: isMobile ? '12px' : '13px'
            }}
          >
            <div>
              {isMatched && <Tag color="success" style={{ marginRight: '4px', fontSize: isMobile ? '11px' : '12px' }}>Match</Tag>}
              <Tag
                color={totalScore >= 90 ? "success" : totalScore >= 70 ? "warning" : "error"}
                style={{ fontSize: isMobile ? '11px' : '12px' }}
              >
                Match: {totalScore}%
              </Tag>
            </div>
            {isMobile && (
              <div onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }} style={{ cursor: 'pointer', marginLeft: 'auto' }}>
                {expanded ? <CompressOutlined style={{ fontSize: '12px' }} /> : <ExpandOutlined style={{ fontSize: '12px' }} />}
              </div>
            )}
          </Space>
        }
      >
        {(expanded || isMatched) && (
          <Space direction="vertical" style={{ width: "100%", gap: isMobile ? '4px' : '6px', paddingLeft: isMobile ? '12px' : '16px' }} size={0}>
            {transfer.coelsaId ? (
              <ComparisonField
                label="COELSA ID"
                extracted={extractedData.coelsaId || ""}
                actual={transfer.coelsaId}
                score={normalizeScore(transfer.scores.identifier)}
              />
            ) : (
              <ComparisonField
                label="Número de operación"
                extracted={extractedData.operationNumber || ""}
                actual={transfer.operationNumber || ""}
                score={normalizeScore(transfer.scores.identifier)}
              />
            )}
            <ComparisonField
              label="Monto: $"
              extracted={extractedData.amount?.toString() || ""}
              actual={transfer.amount?.toString() || ""}
              score={normalizeScore(transfer.scores.amount)}
            />
            <ComparisonField
              label="Fecha/Hora"
              extracted={`${extractedData.date || ""} ${extractedData.time || ""}`}
              actual={new Date(transfer.dateTime).toLocaleString()}
              score={normalizeScore(transfer.scores.time)}
              isDateTime
            />
            {(() => {
              const extractedCuitCuil = extractedData.cuit || extractedData.cuil;
              const transferCuitCuil = transfer.cuit || transfer.cuil;
              const hasCuitCuil = extractedCuitCuil || transferCuitCuil;

              if (hasCuitCuil) {
                return (
                  <ComparisonField
                    label="CUIT/CUIL"
                    extracted={String(extractedCuitCuil || "")}
                    actual={String(transferCuitCuil || "")}
                    score={normalizeScore(transfer.scores.identifier)}
                  />
                );
              }
              return null;
            })()}
          </Space>
        )}
      </Card>
    </motion.div>
  );
});

const INITIAL_BATCH_SIZE = 5;  // Show first 5 transfers immediately
const SUBSEQUENT_BATCH_SIZE = 10; // Load 10 at a time after that

export const ValidationAnalysis: React.FC<ValidationAnalysisProps> = memo(({ requestData }) => {
  const [visibleTransfers, setVisibleTransfers] = useState<AnimatedTransfer[]>([]);
  const [displayedCount, setDisplayedCount] = useState(INITIAL_BATCH_SIZE);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = window.innerWidth < 576;

  const validationStep = useMemo(() => {
    return requestData.processingSteps.find((step) => step.step === "VALIDATION_ANALYSIS");
  }, [requestData]);

  const extractedData = useMemo(() => {
    const data = validationStep?.metadata?.extractedFields ?? {
      date: "",
      time: "",
      amount: 0,
      coelsaId: null,
      operationNumber: "",
      cuit: null,
      cuil: null
    };

    return data;
  }, [validationStep]);

  const transfers = useMemo(() => {
    const comparedTransfers = validationStep?.metadata?.comparedTransfers ?? [];
    return sortTransfers(comparedTransfers, validationStep?.metadata?.matchedTransferId);
  }, [validationStep]);

  useEffect(() => {
    if (!transfers.length) {
      setIsLoading(false);
      return;
    }

    // Show matched transfer first if exists
    const matchedTransferId = validationStep?.metadata?.matchedTransferId;
    const sortedTransfers = transfers.sort((a, b) => {
      if (a.id === matchedTransferId) return -1;
      if (b.id === matchedTransferId) return 1;
      return 0;
    });

    // Load initial batch immediately
    setVisibleTransfers(
      sortedTransfers.slice(0, INITIAL_BATCH_SIZE).map((transfer) => ({
        transfer,
        isVisible: true,
      }))
    );
    
    setIsLoading(false);
  }, [transfers, validationStep?.metadata?.matchedTransferId]);
  
  useEffect(() => {
    if (displayedCount <= INITIAL_BATCH_SIZE || displayedCount >= transfers.length) return;

    const timeoutId = setTimeout(() => {
      setVisibleTransfers(
        transfers.slice(0, displayedCount).map((transfer) => ({
          transfer,
          isVisible: true,
        }))
      );
    }, 100); // Small delay between batches

    return () => clearTimeout(timeoutId);
  }, [displayedCount, transfers]);


const observerInstance = useRef<IntersectionObserver | null>(null);

const observerRef = useCallback((node: HTMLDivElement | null) => {
  // Limpiar el observer anterior si existe
  if (observerInstance.current) {
    observerInstance.current.disconnect();
  }

  if (!node) return;
  
  // Crear el nuevo observer
  const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && visibleTransfers.length < transfers.length) {
      setDisplayedCount(prev => Math.min(prev + SUBSEQUENT_BATCH_SIZE, transfers.length));
    }
  }, { threshold: 0.5 });

  // Guardar referencia al observer para limpiarlo después
  observerInstance.current = observer;
  
  // Observar el nodo
  observer.observe(node);
  
  // No devolver nada aquí
}, [transfers.length, visibleTransfers.length]);

  const detectedPlatform = validationStep?.metadata?.detectedPlatform ?? "desconocida";
  const matchedTransferId = validationStep?.metadata?.matchedTransferId;

  return (
    <Space direction="vertical" style={{
      width: "100%",
      maxWidth: isMobile ? "100%" : "450px",
      padding: isMobile ? "8px 4px" : "12px",
      marginLeft: isMobile ? "8px" : "24px"
    }}>
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: isMobile ? '16px' : '20px' }}>
          <LoadingOutlined />
          <Typography.Text type="secondary" style={{ marginLeft: '8px', fontSize: isMobile ? '13px' : '14px' }}>
            Cargando análisis...
          </Typography.Text>
        </div>
      ) : (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="extracted-data-header"
            style={{ margin: isMobile ? '0 0 8px 0' : '0 0 12px 0' }}
          >
            <Typography.Text strong style={{ fontSize: isMobile ? '14px' : '15px', display: 'block', marginBottom: isMobile ? '8px' : '10px' }}>
              Datos extraídos del comprobante:
            </Typography.Text>
            <Tag color={detectedPlatform !== "desconocida" ? "processing" : "warning"} style={{ fontSize: isMobile ? '12px' : '13px' }}>
              Plataforma: {detectedPlatform.toUpperCase()}
            </Tag>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            transition={{ duration: 0.3, delay: 0.2 }}
            className="extracted-data-container"
          >
            <ExtractedDataCard extractedData={extractedData} />
          </motion.div>

          <Divider style={{ margin: isMobile ? '8px 0' : '12px 0' }} />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.4 }}
            className="transfers-header"
            style={{ margin: isMobile ? '0 0 6px 0' : '0 0 8px 0' }}
          >
            <Typography.Text strong style={{ fontSize: isMobile ? '13px' : '14px' }}>
              Mostrando {visibleTransfers.length} de {transfers.length} transferencias
            </Typography.Text>
          </motion.div>

          <div
            className="transfers-container"
            style={{
              maxHeight: isMobile ? "400px" : "300px",
              overflowY: "auto",
              overflowX: "hidden",
              display: "flex",
              flexDirection: "column",
              gap: isMobile ? "6px" : "8px",
              WebkitOverflowScrolling: "touch",
              padding: isMobile ? "0 4px 4px 0" : "0 8px 8px 0",
              margin: 0,
            }}
          >
            <AnimatePresence mode="sync">
              {visibleTransfers.map((item, index) => (
                <TransferCard
                  key={`transfer-${item.transfer.id}`}
                  transfer={item.transfer}
                  extractedData={extractedData}
                  index={index}
                  isMatched={item.transfer.id === matchedTransferId}
                  isMobile={isMobile}
                />
              ))}
            </AnimatePresence>

            {/* Loading indicator and observer reference */}
            {visibleTransfers.length < transfers.length && (
              <div ref={observerRef} style={{ padding: isMobile ? "8px" : "10px", textAlign: "center" }}>
                <Space>
                  <LoadingOutlined />
                  <Typography.Text type="secondary" style={{ fontSize: isMobile ? '13px' : '14px' }}>
                    Cargando más transferencias...
                  </Typography.Text>
                </Space>
              </div>
            )}
          </div>
        </>
      )}
      
      <style>{`
        .transfers-container {
          scrollbar-width: thin;
          scrollbar-color: transparent transparent;
        }

        .transfers-container::-webkit-scrollbar {
          width: 0px;
          background: transparent;
        }

        .transfers-container::-webkit-scrollbar-track {
          background: transparent;
        }

        .transfers-container::-webkit-scrollbar-thumb {
          background: transparent;
          border-radius: 0;
        }

        .transfers-container:hover {
          scrollbar-color: rgba(0, 0, 0, 0.1) transparent;
        }

        .transfers-container:hover::-webkit-scrollbar {
          width: 4px;
        }

        .transfers-container:hover::-webkit-scrollbar-thumb {
          background-color: rgba(0, 0, 0, 0.1);
          border-radius: 4px;
        }

        .transfers-container:hover::-webkit-scrollbar-thumb:hover {
          background-color: rgba(0, 0, 0, 0.2);
        }

        @media (max-width: 576px) {
          .transfer-card .ant-card-head-title {
            padding: 6px 10px;
            font-size: 12px;
          }

          .transfer-card .ant-card-body {
            padding: 8px 10px;
          }

          .extracted-data-card .ant-card-body {
            padding: 8px 12px;
          }

          .comparison-field {
            margin-bottom: 6px;
          }

          .transfer-card-title {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </Space>
  );
});

ValidationAnalysis.displayName = "ValidationAnalysis"
ExtractedDataCard.displayName = "ExtractedDataCard"
TransferCard.displayName = "TransferCard"
TextComparison.displayName = "TextComparison"
ComparisonField.displayName = "ComparisonField"
CharacterComparison.displayName = "CharacterComparison"
ScoreTag.displayName = "ScoreTag"

export type {
  ValidationAnalysisProps,
  ValidationStep,
  ExtractedData,
  AnimatedTransfer,
  TransferScores,
  ComparedTransfer,
  ValidationDetails,
  TextComparisonProps,
  ComparisonFieldProps,
  TransferCardProps,
  CharacterComparisonProps,
}

export { normalizeScore, calculateTotalScore, sortTransfers }